import { useMutation } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import type { AssignmentDetail, AssignmentShift } from '../../../../../js/api';
import { ApiError, OpenAPI } from '../../../../../js/api';
import { api, request as __request } from '../../../../../js/api/core/request';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { DropdownSelect } from '../../../components/dropdown-select';
import { StatusPill } from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/table';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import { formatDuration, formatShiftDateRange } from '../../../utils/date';
import { SHIFT_TYPE_ICONS, SHIFT_TYPE_ICON_COLORS } from '../../../utils/utils';
import { canUseWorkflowItem, useAudience } from '../../../utils/workflowAccess';
import { ShiftResponseToggle, type ShiftResponseValue } from './ShiftResponseToggle';

function formatMoney(value: number | null | undefined): string {
  if (value == null || value === 0) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const TERMINAL_STATUSES = new Set(['DECLINED', 'WITHDRAWN', 'CANCELED']);
const POSITIVE_TARGET = 'ACCEPTED';
const NEGATIVE_TARGETS = ['DECLINED', 'WITHDRAWN'];

interface ShiftInteractionState {
  canAccept: boolean;
  canNegative: boolean;
  negativeCode: string | null;
  negativeLabel: string;
  isTerminal: boolean;
}

function getShiftInteraction(
  shift: AssignmentShift,
  workflowStatuses: Record<string, any>,
  audience: string
): ShiftInteractionState {
  const currentCode = shift.current_status?.code;
  if (!currentCode || !workflowStatuses[currentCode]) {
    return {
      canAccept: false,
      canNegative: false,
      negativeCode: null,
      negativeLabel: 'Decline',
      isTerminal: false,
    };
  }

  const isTerminal = TERMINAL_STATUSES.has(currentCode);
  if (isTerminal) {
    return {
      canAccept: false,
      canNegative: false,
      negativeCode: null,
      negativeLabel: 'Decline',
      isTerminal,
    };
  }

  const status = workflowStatuses[currentCode];
  const transitions: string[] = status.allowed_transitions || [];

  // Check accept: is ACCEPTED an allowed transition the provider can set?
  const acceptStatus = workflowStatuses[POSITIVE_TARGET];
  const canAccept =
    transitions.includes(POSITIVE_TARGET) &&
    !!acceptStatus &&
    canUseWorkflowItem(audience as any, acceptStatus);

  // Check negative: find first of DECLINED/WITHDRAWN that is allowed
  let canNegative = false;
  let negativeCode: string | null = null;
  let negativeLabel = 'Decline';
  for (const code of NEGATIVE_TARGETS) {
    const targetStatus = workflowStatuses[code];
    if (
      transitions.includes(code) &&
      targetStatus &&
      canUseWorkflowItem(audience as any, targetStatus)
    ) {
      canNegative = true;
      negativeCode = code;
      negativeLabel = code === 'WITHDRAWN' ? 'Withdraw' : 'Decline';
      break;
    }
  }

  return { canAccept, canNegative, negativeCode, negativeLabel, isTerminal };
}

export const ShiftResponseForm: React.FC<{
  assignment: AssignmentDetail;
  onSuccess: () => void;
  onDirtyChange?: (hasDirty: boolean) => void;
  isLockedOut?: boolean;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}> = ({ assignment, onSuccess, onDirtyChange, isLockedOut = false }) => {
  const shifts = assignment.shifts || [];
  const assignmentId = assignment.id;
  const { data: shiftTypeLabels = {} } = useCodeDefinitionsAsMap('ShiftRateTypes');
  const isMobile = useIsMobile();
  const audience = useAudience();

  // Workflow metadata — assignment scope for decline reasons, shift scope for transitions
  const { statuses: assignmentStatuses } = useWorkflowMetadata(WorkflowScopes.Assignment);
  const { statuses: shiftStatuses } = useWorkflowMetadata(WorkflowScopes.AssignmentShift);

  // Initial response detection: assignment is NEW
  const isInitialResponse = assignment.current_status?.code === StatusCodes.NEW;
  const isInProviderReview = assignment.current_status?.code === StatusCodes.PROV_REV;

  // Decline reasons (only for initial full-decline — uses assignment-level workflow)
  const declineStatusConfig = assignmentStatuses?.['DECLINED'];
  const declineReasons = useMemo(() => {
    if (!declineStatusConfig?.reasons) return [];
    return declineStatusConfig.reasons.filter((r: any) => canUseWorkflowItem(audience as any, r));
  }, [declineStatusConfig, audience]);
  const declineReasonOptions = useMemo(
    () => declineReasons.map((r: any) => ({ value: r.code, label: r.title })),
    [declineReasons]
  );
  const requiresReason = declineStatusConfig?.require_reason ?? false;
  const requiresNote = declineStatusConfig?.require_note ?? false;
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const selectedReason = useMemo(
    () => declineReasons.find((r: any) => r.code === reasonCode),
    [declineReasons, reasonCode]
  );
  const reasonRequiresNote = selectedReason?.require_note ?? false;

  // Per-shift interaction capabilities
  const shiftInteractions = useMemo(() => {
    const map: Record<string, ShiftInteractionState> = {};
    for (const shift of shifts) {
      map[shift.id] = getShiftInteraction(shift, shiftStatuses || {}, audience);
    }
    return map;
  }, [shifts, shiftStatuses, audience]);

  // Dirty state: shift ID → target status code, null = unchanged
  const [dirtyShifts, setDirtyShifts] = useState<Record<string, string | null>>({});
  const [comment, setComment] = useState('');

  // Default all acceptable shifts to ACCEPTED on initial response
  const hasDefaulted = useRef(false);
  const someCanAccept = useMemo(
    () => Object.values(shiftInteractions).some((i) => i.canAccept),
    [shiftInteractions]
  );
  useEffect(() => {
    if (isInitialResponse && !hasDefaulted.current && someCanAccept) {
      hasDefaulted.current = true;
      const defaults: Record<string, string> = {};
      for (const [id, interaction] of Object.entries(shiftInteractions)) {
        if (interaction.canAccept) {
          defaults[id] = POSITIVE_TARGET;
        }
      }
      setDirtyShifts(defaults);
    }
  }, [isInitialResponse, someCanAccept, shiftInteractions]);

  // Notify parent whether any shifts have been selected
  const hasDirtyShifts = Object.keys(dirtyShifts).length > 0;
  useEffect(() => {
    // For initial response, auto-defaults don't count — only report after user deviates
    if (isInitialResponse) {
      // Check if user has changed anything from the defaults (all-accept)
      const allDefaultAccept = Object.values(dirtyShifts).every((v) => v === POSITIVE_TARGET);
      onDirtyChange?.(!allDefaultAccept && hasDirtyShifts);
    } else {
      onDirtyChange?.(hasDirtyShifts);
    }
  }, [hasDirtyShifts, dirtyShifts, isInitialResponse, onDirtyChange]);

  const sortedShifts = useMemo(
    () =>
      [...shifts].sort((a, b) => new Date(a.start_on).getTime() - new Date(b.start_on).getTime()),
    [shifts]
  );

  const uniqueFacilities = new Set(shifts.map((s) => s.facility?.id).filter(Boolean));
  const showFacility = uniqueFacilities.size > 1;
  const showBonus = shifts.some((s) => s.bonus_amount && s.bonus_amount > 0);
  const showHoliday = shifts.some((s) => s.holiday_amount && s.holiday_amount > 0);

  // Convert dirty state to ShiftResponseValue for toggle
  const getToggleValue = (shiftId: string): ShiftResponseValue => {
    const targetCode = dirtyShifts[shiftId];
    if (targetCode === undefined || targetCode === null) return null;
    if (targetCode === POSITIVE_TARGET) return 'accept';
    return 'decline'; // DECLINED or WITHDRAWN both map to 'decline' visually
  };

  const handleToggle = (shiftId: string, value: ShiftResponseValue) => {
    const interaction = shiftInteractions[shiftId];
    if (!interaction) return;
    setDirtyShifts((prev) => {
      const next = { ...prev };
      if (value === null) {
        delete next[shiftId];
      } else if (value === 'accept' && interaction.canAccept) {
        next[shiftId] = POSITIVE_TARGET;
      } else if (value === 'decline' && interaction.canNegative && interaction.negativeCode) {
        next[shiftId] = interaction.negativeCode;
      }
      return next;
    });
  };

  // Bulk actions for initial response
  const handleSetAll = (value: ShiftResponseValue) => {
    const updated: Record<string, string | null> = {};
    for (const shift of shifts) {
      const interaction = shiftInteractions[shift.id];
      if (!interaction) continue;
      if (value === 'accept' && interaction.canAccept) {
        updated[shift.id] = POSITIVE_TARGET;
      } else if (value === 'decline' && interaction.canNegative && interaction.negativeCode) {
        updated[shift.id] = interaction.negativeCode;
      }
    }
    setDirtyShifts(updated);
  };

  // Derived state
  const dirtyEntries = Object.entries(dirtyShifts).filter(([, code]) => code !== null);
  const hasAnyNegativeDirty = dirtyEntries.some(
    ([, code]) => code === 'DECLINED' || code === 'WITHDRAWN'
  );

  // Initial response specific: check if all actionable shifts are set
  const actionableShiftIds = useMemo(
    () =>
      shifts
        .filter((s) => shiftInteractions[s.id]?.canAccept || shiftInteractions[s.id]?.canNegative)
        .map((s) => s.id),
    [shifts, shiftInteractions]
  );
  const allActionableSelected =
    isInitialResponse && actionableShiftIds.every((id) => dirtyShifts[id] != null);
  const allAccepted =
    isInitialResponse &&
    allActionableSelected &&
    actionableShiftIds.every((id) => dirtyShifts[id] === POSITIVE_TARGET);
  const allDeclined =
    isInitialResponse &&
    allActionableSelected &&
    actionableShiftIds.every((id) => dirtyShifts[id] === 'DECLINED');

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: (payload: {
      shifts: { id: string; code: string }[];
      reason_code: string;
      comment: string;
    }) =>
      __request(OpenAPI, {
        method: 'PATCH',
        url: '/api/assignments/assignment/{id}/provider-shift-status-update/',
        path: { id: assignmentId },
        body: payload,
        mediaType: 'application/json',
      }) as Promise<AssignmentDetail>,
    onSuccess: () => {
      let msg = 'Changes submitted';
      if (isInitialResponse) {
        if (allDeclined) msg = 'Assignment Declined';
        else if (allAccepted) msg = 'Assignment Accepted';
        else msg = 'Assignment Partially Accepted';
      }
      toast.success(msg);
      setDirtyShifts({});
      setComment('');
      setReasonCode(null);
      onSuccess();
    },
    onError: (error) => handleApiError(error),
  });

  const handleSubmit = () => {
    const payload = {
      shifts: dirtyEntries.map(([id, code]) => ({ id, code: code! })),
      reason_code: reasonCode || '',
      comment,
    };
    submitMutation.mutate(payload);
  };

  // Button label and style
  let buttonLabel = 'Submit Changes';
  let buttonColor: 'brandgreen0' | 'stoplight-danger' | 'brand-green-outline' | undefined =
    'brand-green-outline';
  if (isInitialResponse) {
    if (allAccepted) {
      buttonLabel = 'Accept Assignment';
      buttonColor = 'brandgreen0';
    } else if (allDeclined) {
      buttonLabel = 'Decline Assignment';
      buttonColor = 'stoplight-danger';
    } else {
      buttonLabel = 'Partial Accept';
      buttonColor = 'brand-green-outline';
    }
  }

  const showSubmitButton =
    !isLockedOut && hasDirtyShifts && (!isInitialResponse || allActionableSelected);
  const showReasonDropdown = isInitialResponse && allDeclined && declineReasonOptions.length > 0;
  const noteRequired = isInitialResponse && allDeclined && (requiresNote || reasonRequiresNote);
  const showComment = hasAnyNegativeDirty;

  const reasonMissing = isInitialResponse && allDeclined && requiresReason && !reasonCode;
  const noteMissing = noteRequired && !comment.trim();
  const isSubmitDisabled = reasonMissing || noteMissing || submitMutation.isLoading;

  // Any shift that has accept or negative capability and is not locked out
  const hasAnyAcceptable = !isLockedOut && shifts.some((s) => shiftInteractions[s.id]?.canAccept);
  const hasAnyNegative = !isLockedOut && shifts.some((s) => shiftInteractions[s.id]?.canNegative);
  const showToggleColumn = !isLockedOut && (hasAnyAcceptable || hasAnyNegative);

  const isConfirmed = assignment.current_status?.code === StatusCodes.CONFIRMED;
  const hasCanceledShifts = shifts.some((s) => s.current_status?.code === StatusCodes.CANCELED);

  const downloadCalendar = async (url: string) => {
    try {
      await api.get(url, { responseType: 'blob' });
      window.location.assign(url);
    } catch (error: any) {
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        const reader = new FileReader();
        reader.onload = () => {
          const body = JSON.parse(reader.result as string);
          handleApiError(
            new ApiError(
              error.config,
              {
                url: error.config.url,
                status: error.response.status,
                statusText: error.response.statusText,
                body,
                ok: false,
              },
              body.detail || 'Unknown error'
            ),
            { handle403: true }
          );
        };
        reader.readAsText(error.response.data);
      } else {
        handleApiError(error, { handle403: true });
      }
    }
  };

  const handleDownloadCalendar = () =>
    downloadCalendar(`/api/assignments/calendar-ics/${assignmentId}/`);

  const handleDownloadCancellationCalendar = () =>
    downloadCalendar(`/api/assignments/calendar-ics-cancellation/${assignmentId}/`);

  return (
    <div>
      {/* 30-day lockout alert */}
      {isLockedOut && (
        <div className="tw:mb-4 tw:p-4 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:text-sm tw:text-stoplight-danger">
          <div className="tw:flex tw:items-start tw:gap-2">
            <i className="fas fa-exclamation-triangle tw:text-stoplight-danger tw:mt-0.5" />
            <div>
              <p className="tw:font-medium tw:!mb-1">
                This assignment is within the contracted 30 day window.
              </p>
              <p className="tw:!mb-0">
                If you have a conflict or need to request changes, please contact Sendit Support:
              </p>
              {window.SENDIT_CONTACT_INFO?.provider_support_email && (
                <p className="tw:!mb-0">
                  Email:{' '}
                  <a
                    href={`mailto:${window.SENDIT_CONTACT_INFO.provider_support_email}`}
                    className="tw:underline"
                  >
                    {window.SENDIT_CONTACT_INFO.provider_support_email}
                  </a>
                </p>
              )}
              {window.SENDIT_CONTACT_INFO?.provider_phone_voice && (
                <p className="tw:!mb-0">
                  Phone:{' '}
                  <a
                    href={`tel:${window.SENDIT_CONTACT_INFO.provider_phone_voice}`}
                    className="tw:underline"
                  >
                    {window.SENDIT_CONTACT_INFO.provider_phone_voice}
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Calendar action buttons */}
      {isConfirmed && (
        <div className="tw:flex tw:justify-end tw:gap-2 tw:mb-2">
          <Button small color="brand-green-outline" onClick={handleDownloadCalendar} disabled>
            <i className="far fa-cloud-download tw:mr-1" />
            Add to Calendar
          </Button>
          {hasCanceledShifts && (
            <Button
              small
              color="stoplight-danger-outline"
              onClick={handleDownloadCancellationCalendar}
              disabled
            >
              <i className="far fa-calendar-times tw:mr-1" />
              Sync Cancellations
            </Button>
          )}
        </div>
      )}
      {/* Provider Review */}
      {isInProviderReview && (
        <div className="tw:mb-2 tw:p-2 tw:bg-blue-50 tw:border tw:border-blue-200 tw:rounded-lg tw:text-sm tw:text-blue-700">
          <div className="tw:flex tw:items-start tw:gap-2">
            <i className="fas fa-info-circle tw:text-blue-700 tw:mt-0.5" />
            <div>
              <p className="tw:font-medium tw:!mb-1">Proposed Changes</p>
              <div className="tw:!text-xs">Please review and respond.</div>
              <div className="tw:!text-xs">
                Click the{' '}
                <i className="fas fa-check tw:py-0.5 tw:px-1 tw:border tw:border-blue-700/60 tw:rounded-sm" />{' '}
                to approve or the{' '}
                <i className="fas fa-times tw:py-0.5 tw:px-1 tw:border tw:border-blue-700/60 tw:rounded-sm" />{' '}
                to decline.
              </div>
            </div>
          </div>
        </div>
      )}
      <Table dense striped>
        <TableHead>
          {showToggleColumn && (
            <TableHeader>
              {isInitialResponse ? (
                <div className="tw:flex tw:items-center tw:gap-1">
                  {hasAnyAcceptable && (
                    <button
                      type="button"
                      className="tw:w-5 tw:h-5 tw:flex tw:items-center tw:justify-center tw:!rounded-sm tw:border tw:border-stoplight-success tw:text-stoplight-success tw:bg-white tw:hover:bg-stoplight-success/10 tw:cursor-pointer tw:transition-colors"
                      onClick={() => handleSetAll('accept')}
                      title="Accept All"
                    >
                      <i className="fas fa-check tw:text-[9px]" />
                    </button>
                  )}
                  {hasAnyNegative && (
                    <button
                      type="button"
                      className="tw:w-5 tw:h-5 tw:flex tw:items-center tw:justify-center tw:!rounded-sm tw:border tw:border-stoplight-danger tw:text-stoplight-danger tw:bg-white tw:hover:bg-stoplight-danger/10 tw:cursor-pointer tw:transition-colors"
                      onClick={() => handleSetAll('decline')}
                      title="Decline All"
                    >
                      <i className="fas fa-xmark tw:text-[9px]" />
                    </button>
                  )}
                </div>
              ) : (
                <span />
              )}
            </TableHeader>
          )}
          <TableHeader>Date / Time</TableHeader>
          <TableHeader>{isMobile ? '' : 'Duration'}</TableHeader>
          <TableHeader className="tw:!text-center">Rate Type</TableHeader>
          <TableHeader className="tw:!text-right">Amount</TableHeader>
          {showBonus && <TableHeader className="tw:!text-right">Bonus</TableHeader>}
          {showHoliday && <TableHeader className="tw:!text-right">Holiday</TableHeader>}
          <TableHeader>Status</TableHeader>
        </TableHead>
        <TableBody>
          {sortedShifts.map((shift) => {
            const interaction = shiftInteractions[shift.id];
            const isTerminal = interaction?.isTerminal ?? false;
            const strikethrough = isTerminal ? 'tw:line-through tw:text-zinc-400' : '';
            const toggleValue = getToggleValue(shift.id);

            return (
              <TableRow
                key={shift.id}
                className={clsx(
                  isInProviderReview ? 'tw:odd:bg-transparent' : 'tw:odd:bg-brand-green-3',
                  isInProviderReview &&
                    shift.current_status.code === StatusCodes.PROPOSED &&
                    'tw:!bg-blue-50 tw:!text-blue-700'
                )}
              >
                {showToggleColumn && (
                  <TableCell>
                    {interaction?.canAccept || interaction?.canNegative ? (
                      <ShiftResponseToggle
                        value={toggleValue}
                        onChange={(v) => handleToggle(shift.id, v)}
                        acceptDisabled={!interaction?.canAccept}
                        declineDisabled={!interaction?.canNegative}
                        declineTooltip={interaction?.negativeLabel || 'Decline'}
                      />
                    ) : null}
                  </TableCell>
                )}
                <TableCell className={`tw:whitespace-nowrap ${strikethrough}`}>
                  <div>
                    {formatShiftDateRange(
                      shift.start_on,
                      shift.start_time,
                      shift.end_on,
                      shift.end_time,
                      'M/d/yyyy'
                    )}
                  </div>
                  <div className="tw:text-[11px] tw:leading-tight tw:text-zinc-400">
                    <span className="tw:font-medium">
                      {format(parseISO(shift.start_on), 'EEE')}
                    </span>
                    {showFacility && shift.facility?.name && (
                      <>
                        <span className="tw:mx-1">·</span>
                        {shift.facility.name !== assignment.facility?.name && (
                          <i className="far fa-info-circle tw:mr-1 tw:text-blue-600" />
                        )}
                        <span className="tw:text-zinc-500">{shift.facility.name}</span>
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell className={strikethrough}>{formatDuration(shift.duration)}</TableCell>
                <TableCell className={strikethrough}>
                  <div className="tw:flex tw:flex-col tw:items-center tw:leading-tight">
                    <i
                      className={`fas tw:text-lg ${SHIFT_TYPE_ICONS[shift.shift_rate_type as keyof typeof SHIFT_TYPE_ICONS] || ''} ${SHIFT_TYPE_ICON_COLORS[shift.shift_rate_type as keyof typeof SHIFT_TYPE_ICON_COLORS] || ''}`}
                    />
                    <span className="tw:text-[10px] tw:text-zinc-500">
                      {shiftTypeLabels[shift.shift_rate_type] || shift.shift_rate_type}
                    </span>
                  </div>
                </TableCell>
                <TableCell className={`tw:text-right ${strikethrough}`}>
                  {formatMoney(Number(shift.hourly_amount))}
                </TableCell>
                {showBonus && (
                  <TableCell className={`tw:text-right ${strikethrough}`}>
                    {formatMoney(shift.bonus_amount)}
                  </TableCell>
                )}
                {showHoliday && (
                  <TableCell className={`tw:text-right ${strikethrough}`}>
                    {formatMoney(shift.holiday_amount)}
                  </TableCell>
                )}
                <TableCell>
                  <StatusPill
                    scope={WorkflowScopes.AssignmentShift}
                    status={shift.current_status}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Reason + Comment — shown when any dirty shift is a decline/withdraw */}
      {showComment && (
        <div className="tw:mt-4 tw:px-1 tw:space-y-3">
          {showReasonDropdown && (
            <div>
              <label
                htmlFor="shift-response-reason"
                className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
              >
                Reason
                {requiresReason && <span className="tw:text-stoplight-danger"> *</span>}
              </label>
              <DropdownSelect
                inputId="shift-response-reason"
                aria-label="Decline reason"
                allOptions={declineReasonOptions}
                value={reasonCode}
                isSearchable={false}
                isClearable={true}
                placeholder="Select reason..."
                onChange={(opt: any) => setReasonCode(opt?.value ?? null)}
              />
            </div>
          )}
          <div>
            <label
              htmlFor="shift-response-comment"
              className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
            >
              Comment
              {noteRequired && <span className="tw:text-stoplight-danger"> *</span>}
            </label>
            <textarea
              id="shift-response-comment"
              className="tw:w-full tw:rounded-lg tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:placeholder-zinc-400 tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
              rows={3}
              placeholder="Add a note about your response..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Submit button */}
      {showSubmitButton && (
        <div className="tw:mt-4 tw:flex tw:justify-end tw:px-1">
          <Button color={buttonColor} disabled={isSubmitDisabled} onClick={handleSubmit}>
            {submitMutation.isLoading ? 'Submitting...' : buttonLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
